import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Public type (kept same exported name for external compatibility)
export interface MasonryItemDef { id: string; render: () => React.ReactNode; }
interface MasonryProps { items: MasonryItemDef[]; }

interface PlacedItem extends MasonryItemDef { height: number; }

// Configuration
const TARGET_COLUMN_WIDTH = 300; // px (wider cards per request)
const MAX_COLUMNS = 8;
const MIN_COLUMNS = 1;

export const DashboardMasonry: React.FC<MasonryProps> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [columns, setColumns] = useState<PlacedItem[][]>([]);
  const sizeRef = useRef<{ width: number; colCount: number }>({ width: 0, colCount: 0 });

  // Measure & compute number of columns based on width
  const computeColumnCount = useCallback((width: number) => {
    const available = Math.max(width, TARGET_COLUMN_WIDTH);
    const raw = Math.floor(available / TARGET_COLUMN_WIDTH);
    return Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, raw || 1));
  }, []);

  const performLayout = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const width = el.clientWidth;
    const colCount = computeColumnCount(width);
    // Collect heights
    const heights: Record<string, number> = {};
    itemRefs.current.forEach((node, id) => {
      heights[id] = node?.offsetHeight || 0;
    });
    // Initialize columns
    const working: PlacedItem[][] = Array.from({ length: colCount }, () => []);
    const colHeights = new Array(colCount).fill(0);
    // Greedy placement: always append to shortest column
    items.forEach(it => {
      const h = heights[it.id] ?? 0;
      let target = 0;
      for (let i = 1; i < colCount; i++) {
        if (colHeights[i] < colHeights[target]) target = i;
      }
      working[target].push({ ...it, height: h });
      colHeights[target] += h;
    });
    sizeRef.current = { width, colCount };
    setColumns(working);
  }, [items, computeColumnCount]);

  // Initial layout & on resize
  useLayoutEffect(() => { performLayout(); }, [performLayout]);

  useEffect(() => {
    const ro = new ResizeObserver(() => performLayout());
    const el = containerRef.current;
    if (el) ro.observe(el);
    return () => ro.disconnect();
  }, [performLayout]);

  // Re-layout when item heights change (expand/collapse). Observe individual items.
  useEffect(() => {
    const ro = new ResizeObserver(() => performLayout());
    itemRefs.current.forEach(node => node && ro.observe(node));
    return () => ro.disconnect();
  }, [columns, performLayout]);

  // If columns not computed yet (first pass), render simple linear list so heights can be measured
  if (!columns.length) {
    return (
      <div className="dashboard-masonry measuring" ref={containerRef}>
        {items.map(item => (
          <div
            key={item.id}
            className="dashboard-card-shell"
            ref={el => { if (el) itemRefs.current.set(item.id, el); }}>
            {item.render()}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="dashboard-masonry dynamic" ref={containerRef}>
      {columns.map((col, ci) => (
        <div className="dashboard-masonry-column" key={ci} data-col-index={ci}>
          {col.map(item => (
            <div
              key={item.id}
              className="dashboard-card-shell"
              ref={el => { if (el) itemRefs.current.set(item.id, el); }}>
              {item.render()}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};